import { Link, useLocation } from 'react-router-dom';
import { FaTachometerAlt, FaComments, FaUsers, FaCog, FaChartLine, FaShieldAlt, FaTrash, FaDatabase, FaKey, FaExclamationTriangle, FaEdit, FaEnvelope, FaFlag } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const AdminDashboard = () => {
  const location = useLocation();

  // Cache management states
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheKeys, setCacheKeys] = useState([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [showCacheModal, setShowCacheModal] = useState(false);
  const [message, setMessage] = useState(null);
  
  // User management states
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editGroups, setEditGroups] = useState([]);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [userLoading, setUserLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    loadUsers();
    loadGroups();
  }, []);

  // Load groups function
  const loadGroups = async () => {
    try {
      const res = await axiosInstance.get('/admin/groups');
      setGroups(res.data);
    } catch (err) {
      console.error('Error loading groups:', err);
      // Groups are optional, so we don't show an error message
    }
  };

  // Cache management functions
  const loadCacheStats = async () => {
    setCacheLoading(true);
    try {
      const res = await axiosInstance.get('/admin/cache/stats');
      setCacheStats(res.data);
      setMessage({ type: 'success', text: 'Cache-Statistiken erfolgreich geladen!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden der Cache-Statistiken.' });
    } finally {
      setCacheLoading(false);
    }
  };

  const loadCacheKeys = async () => {
    setCacheLoading(true);
    try {
      const res = await axiosInstance.get('/admin/cache/keys');
      setCacheKeys(res.data.keys || []);
      setShowCacheModal(true);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden der Cache-Keys.' });
    } finally {
      setCacheLoading(false);
    }
  };

  const clearAllCache = async () => {
    if (!window.confirm('Sind Sie sicher, dass Sie alle Caches löschen möchten?')) return;
    
    setCacheLoading(true);
    try {
      await axiosInstance.delete('/admin/cache/clear');
      setMessage({ type: 'success', text: 'Alle Caches erfolgreich gelöscht!' });
      loadCacheStats();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Löschen der Caches.' });
    } finally {
      setCacheLoading(false);
    }
  };

  const clearSpecificCache = async (key) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie den Cache "${key}" löschen möchten?`)) return;
    
    setCacheLoading(true);
    try {
      await axiosInstance.delete(`/admin/cache/clear/${encodeURIComponent(key)}`);
      setMessage({ type: 'success', text: `Cache "${key}" erfolgreich gelöscht!` });
      loadCacheKeys();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Löschen des Caches.' });
    } finally {
      setCacheLoading(false);
    }
  };

  // User management functions
  const loadUsers = async () => {
    setUserLoading(true);
    try {
      const res = await axiosInstance.get('/admin/users');
      setUsers(res.data.slice(0, 10)); // Show only first 10 users in dashboard
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden der Benutzer.' });
    } finally {
      setUserLoading(false);
    }
  };

  const openUserEditModal = (user) => {
    setSelectedUser(user);
    setEditUsername(user.username);
    setEditPassword('');
    setEditGroups(user.groups?.map(g => g._id) || []);
    setEditIsAdmin(user.isAdmin || false);
    setShowUserModal(true);
  };

  const updateUser = async () => {
    if (!selectedUser) return;
    
    // Validate input
    const usernameErrors = validateUsername(editUsername);
    const passwordErrors = validatePassword(editPassword);
    
    if (usernameErrors.length > 0 || passwordErrors.length > 0) {
      const allErrors = [...usernameErrors, ...passwordErrors];
      setMessage({ type: 'error', text: allErrors.join(', ') });
      return;
    }
    
    setUserLoading(true);
    try {
      const updateData = {};
      
      // Check for username changes
      if (editUsername !== selectedUser.username) {
        updateData.username = editUsername;
      }
      
      // Check for password changes
      if (editPassword) {
        updateData.password = editPassword;
      }
      
      // Check for group changes
      const originalGroups = selectedUser.groups?.map(g => g._id) || [];
      if (JSON.stringify(editGroups.sort()) !== JSON.stringify(originalGroups.sort())) {
        updateData.groups = editGroups;
      }
      
      // Check for admin status changes
      if (editIsAdmin !== (selectedUser.isAdmin || false)) {
        updateData.isAdmin = editIsAdmin;
      }
      
      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'warning', text: 'Keine Änderungen vorgenommen.' });
        setShowUserModal(false);
        return;
      }
      
      await axiosInstance.put(`/users/${selectedUser._id}`, updateData);
      
      // Show success message
      setMessage({ type: 'success', text: 'Benutzer erfolgreich aktualisiert!' });
      setShowUserModal(false);
      setSelectedUser(null);
      setEditUsername('');
      setEditPassword('');
      setEditGroups([]);
      setEditIsAdmin(false);
      loadUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Fehler beim Aktualisieren des Benutzers.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setUserLoading(false);
    }
  };

  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Validation functions
  const validateUsername = (username) => {
    const errors = [];
    if (!username || username.length < 3) {
      errors.push('Benutzername muss mindestens 3 Zeichen lang sein');
    }
    if (username.length > 30) {
      errors.push('Benutzername darf maximal 30 Zeichen lang sein');
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      errors.push('Benutzername darf nur Buchstaben, Zahlen, Unterstriche und Bindestriche enthalten');
    }
    return errors;
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password && password.length < 6) {
      errors.push('Passwort muss mindestens 6 Zeichen lang sein');
    }
    if (password && password.length > 128) {
      errors.push('Passwort darf maximal 128 Zeichen lang sein');
    }
    return errors;
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: <FaTachometerAlt />, color: 'from-blue-500 to-blue-600' },
    { path: '/admin/forums', label: 'Forenverwaltung', icon: <FaComments />, color: 'from-green-500 to-green-600' },
    { path: '/admin/users', label: 'Benutzerverwaltung', icon: <FaUsers />, color: 'from-purple-500 to-purple-600' },
    { path: '/admin/groups', label: 'Gruppenverwaltung', icon: <FaShieldAlt />, color: 'from-orange-500 to-orange-600' },
    { path: '/admin/custom-fields', label: 'Profil-Felder', icon: <FaEdit />, color: 'from-teal-500 to-teal-600' },
    { path: '/admin/warnings', label: 'Verwarnsystem', icon: <FaExclamationTriangle />, color: 'from-red-500 to-red-600' },
    { path: '/admin/reports', label: 'Meldungen', icon: <FaFlag />, color: 'from-pink-500 to-pink-600' },
    { path: '/admin/pm', label: 'PM-Übersicht', icon: <FaEnvelope />, color: 'from-cyan-500 to-cyan-600' },
    { path: '/admin/settings', label: 'Einstellungen', icon: <FaCog />, color: 'from-gray-500 to-gray-600' },
    { path: '/admin/cms', label: 'CMS', icon: <FaChartLine />, color: 'from-indigo-500 to-indigo-600' },
  ];

  const stats = [
    { title: 'Foren', count: 12, icon: <FaComments />, link: '/admin/forums', color: 'from-green-500 to-emerald-600', description: 'Aktive Foren' },
    { title: 'Benutzer', count: 84, icon: <FaUsers />, link: '/admin/users', color: 'from-blue-500 to-cyan-600', description: 'Registrierte Nutzer' },
    { title: 'Beiträge', count: 1247, icon: <FaComments />, link: '/admin/posts', color: 'from-purple-500 to-pink-600', description: 'Veröffentlichte Beiträge' },
    { title: 'Heute aktiv', count: 23, icon: <FaTachometerAlt />, link: '/admin/activity', color: 'from-orange-500 to-red-600', description: 'Aktive Benutzer heute' },
  ];

  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="min-h-screen">
      <div className="flex">
        {/* Modern Sidebar */}
        <aside className="w-72 bg-white/80 backdrop-blur-lg border-r border-gray-200/50 min-h-screen shadow-xl">
          <div className="p-6">
            <div className="flex items-center mb-8">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-green to-green-600 rounded-xl flex items-center justify-center mr-3">
                <FaTachometerAlt className="text-white text-lg" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-800">Admin Panel</h4>
                <p className="text-sm text-gray-500">Systemverwaltung</p>
              </div>
            </div>
            
            <nav className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 group ${
                    location.pathname === item.path 
                      ? 'bg-gradient-to-r from-primary-green to-green-600 text-white shadow-lg' 
                      : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    location.pathname === item.path 
                      ? 'bg-white/20' 
                      : `bg-gradient-to-r ${item.color} text-white group-hover:scale-110 transition-transform`
                  }`}>
                    {item.icon}
                  </div>
                  <span className="font-medium">{item.label}</span>
                  {location.pathname === item.path && (
                    <div className="ml-auto w-2 h-2 bg-white rounded-full"></div>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-grow p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-600">Übersicht über Ihr Forum-System</p>
          </div>

          {/* Alert Messages */}
          {message && (
            <div className={`alert mb-6 ${message.type === 'success' ? 'alert-success' : message.type === 'warning' ? 'alert-warning' : 'alert-error'}`}>
              {message.text}
            </div>
          )}

          {/* Quick Action Buttons */}
          <div className="mb-8">
            <div className="modern-card p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaDatabase className="mr-2 text-blue-600" />
                Cache-Verwaltung
              </h3>
              <div className="flex flex-wrap gap-4 mb-4">
                <button 
                  onClick={loadCacheStats}
                  disabled={cacheLoading}
                  className="btn btn-info flex items-center gap-2"
                >
                  <FaDatabase />
                  {cacheLoading ? 'Lädt...' : 'Cache-Statistiken laden'}
                </button>
                <button 
                  onClick={loadCacheKeys}
                  disabled={cacheLoading}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <FaKey />
                  Cache-Keys anzeigen
                </button>
                <button 
                  onClick={clearAllCache}
                  disabled={cacheLoading}
                  className="btn btn-error flex items-center gap-2"
                >
                  <FaTrash />
                  Alle Caches löschen
                </button>
              </div>

              {/* Cache Statistics Display */}
              {cacheStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{cacheStats.keyCount || 0}</div>
                    <div className="text-sm text-gray-600">Anzahl Keys</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{cacheStats.memoryUsage || 'N/A'}</div>
                    <div className="text-sm text-gray-600">Speicherverbrauch</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{cacheStats.hits || 0}</div>
                    <div className="text-sm text-gray-600">Cache Hits</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{cacheStats.misses || 0}</div>
                    <div className="text-sm text-gray-600">Cache Misses</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="modern-card p-6 group hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                    {stat.icon}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800">{stat.count}</div>
                    <div className="text-sm text-gray-500">{stat.description}</div>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">{stat.title}</h3>
                <Link 
                  to={stat.link} 
                  className="text-primary-green hover:text-primary-green-dark font-medium text-sm transition-colors flex items-center group"
                >
                  Verwalten
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* User Management */}
            <div className="modern-card p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaUsers className="mr-2 text-purple-600" />
                Schnelle Benutzerverwaltung
              </h3>
              {userLoading ? (
                <div className="flex justify-center py-4">
                  <div className="loading loading-spinner loading-md"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.slice(0, 5).map((user) => (
                    <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-800">{user.username}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.isAdmin && (
                          <span className="badge badge-success badge-sm">Admin</span>
                        )}
                        <button
                          onClick={() => openUserEditModal(user)}
                          className="btn btn-outline btn-primary btn-sm"
                        >
                          Bearbeiten
                        </button>
                      </div>
                    </div>
                  ))}
                  <Link to="/admin/users" className="btn btn-outline btn-primary w-full">
                    Alle Benutzer verwalten
                  </Link>
                </div>
              )}
            </div>

            {/* Recent Activity - existing */}
            <div className="modern-card p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                Letzte Aktivitäten
              </h3>
              <div className="space-y-3">
                {[
                  { user: 'MaxMustermann', action: 'hat ein neues Thema erstellt', time: 'vor 2 Minuten', type: 'post' },
                  { user: 'AdminUser', action: 'hat Benutzerrechte geändert', time: 'vor 15 Minuten', type: 'admin' },
                  { user: 'TestUser123', action: 'hat sich registriert', time: 'vor 1 Stunde', type: 'user' },
                  { user: 'ForumMod', action: 'hat einen Beitrag moderiert', time: 'vor 2 Stunden', type: 'mod' },
                ].map((activity, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                      activity.type === 'post' ? 'bg-blue-100 text-blue-600' :
                      activity.type === 'admin' ? 'bg-red-100 text-red-600' :
                      activity.type === 'user' ? 'bg-green-100 text-green-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {activity.type === 'post' ? <FaComments className="text-xs" /> :
                       activity.type === 'admin' ? <FaCog className="text-xs" /> :
                       activity.type === 'user' ? <FaUsers className="text-xs" /> :
                       <FaShieldAlt className="text-xs" />}
                    </div>
                    <div className="flex-grow">
                      <p className="text-sm text-gray-800">
                        <span className="font-medium">{activity.user}</span> {activity.action}
                      </p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* System Status */}
            <div className="modern-card p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                System-Status
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Server-Status', status: 'Online', color: 'green', percentage: 100 },
                  { label: 'Datenbank', status: 'Verbunden', color: 'green', percentage: 98 },
                  { label: 'Speicherplatz', status: '78% belegt', color: 'yellow', percentage: 78 },
                  { label: 'Cache', status: 'Optimiert', color: 'green', percentage: 92 },
                ].map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">{item.label}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.color === 'green' ? 'bg-green-100 text-green-800' :
                        item.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${
                          item.color === 'green' ? 'bg-green-500' :
                          item.color === 'yellow' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Cache Keys Modal */}
      {showCacheModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-4xl">
            <h3 className="font-bold text-lg mb-4">Cache-Keys</h3>
            
            {cacheKeys.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">Keine Cache-Keys gefunden.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {cacheKeys.map((key, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <code className="text-sm text-gray-800">{key}</code>
                    <button
                      onClick={() => clearSpecificCache(key)}
                      disabled={cacheLoading}
                      className="btn btn-outline btn-error btn-sm"
                    >
                      Löschen
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-action">
              <button 
                onClick={() => setShowCacheModal(false)}
                className="btn btn-ghost"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* User Edit Modal */}
      {showUserModal && selectedUser && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-xl mb-6 text-gray-800">
              Benutzer bearbeiten
            </h3>
            
            {/* User Info Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <FaUsers className="text-white text-lg" />
                </div>
                <div>
                  <div className="font-semibold text-gray-800">{selectedUser.username}</div>
                  <div className="text-sm text-gray-600">{selectedUser.email}</div>
                  <div className="text-xs text-gray-500">
                    {selectedUser.isAdmin ? (
                      <span className="badge badge-success badge-sm">Administrator</span>
                    ) : (
                      <span className="badge badge-ghost badge-sm">Benutzer</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); updateUser(); }} className="space-y-6">
              {/* Username Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Benutzername
                </label>
                <input
                  type="text"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  className={`input input-bordered w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validateUsername(editUsername).length > 0 ? 'border-red-500' : ''
                  }`}
                  placeholder="Benutzername eingeben"
                  required
                />
                <div className="mt-1">
                  {validateUsername(editUsername).length > 0 ? (
                    <div className="text-xs text-red-600 space-y-1">
                      {validateUsername(editUsername).map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">
                      3-30 Zeichen, nur Buchstaben, Zahlen, _ und - erlaubt
                    </div>
                  )}
                </div>
              </div>
              
              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Neues Passwort
                </label>
                <input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  className={`input input-bordered w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validatePassword(editPassword).length > 0 ? 'border-red-500' : ''
                  }`}
                  placeholder="Leer lassen, um Passwort nicht zu ändern"
                />
                <div className="mt-1">
                  {validatePassword(editPassword).length > 0 ? (
                    <div className="text-xs text-red-600 space-y-1">
                      {validatePassword(editPassword).map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">
                      {editPassword ? 'Mindestens 6 Zeichen' : 'Optional - leer lassen für keine Änderung'}
                    </div>
                  )}
                </div>
              </div>

              {/* Groups Field */}
              {groups.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Gruppen
                  </label>
                  <select
                    multiple
                    className="select select-bordered w-full h-32 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editGroups}
                    onChange={(e) =>
                      setEditGroups(Array.from(e.target.selectedOptions, (opt) => opt.value))
                    }
                  >
                    {groups.map(group => (
                      <option key={group._id} value={group._id}>{group.name}</option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    Halten Sie Strg/Cmd gedrückt, um mehrere Gruppen auszuwählen
                  </div>
                </div>
              )}

              {/* Admin Rights Field */}
              <div>
                <label className="cursor-pointer label">
                  <span className="label-text font-semibold text-gray-700 flex items-center gap-2">
                    <FaShieldAlt className="text-orange-600" />
                    Administrator-Rechte
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={editIsAdmin}
                    onChange={(e) => setEditIsAdmin(e.target.checked)}
                  />
                </label>
              </div>

              {/* Changes Preview */}
              {(editUsername !== selectedUser.username || editPassword || 
                JSON.stringify(editGroups.sort()) !== JSON.stringify((selectedUser.groups?.map(g => g._id) || []).sort()) ||
                editIsAdmin !== (selectedUser.isAdmin || false)) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                    <FaCog className="mr-2" />
                    Geplante Änderungen:
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {editUsername !== selectedUser.username && (
                      <li>• Benutzername: "{selectedUser.username}" → "{editUsername}"</li>
                    )}
                    {editPassword && (
                      <li>• Passwort wird geändert</li>
                    )}
                    {JSON.stringify(editGroups.sort()) !== JSON.stringify((selectedUser.groups?.map(g => g._id) || []).sort()) && (
                      <li>• Gruppenzugehörigkeit wird geändert</li>
                    )}
                    {editIsAdmin !== (selectedUser.isAdmin || false) && (
                      <li>• Admin-Status: {selectedUser.isAdmin ? 'Administrator' : 'Benutzer'} → {editIsAdmin ? 'Administrator' : 'Benutzer'}</li>
                    )}
                  </ul>
                </div>
              )}
            </form>
            
            <div className="modal-action pt-6">
              <button 
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUser(null);
                  setEditUsername('');
                  setEditPassword('');
                  setEditGroups([]);
                  setEditIsAdmin(false);
                }}
                className="btn btn-ghost"
                disabled={userLoading}
              >
                Abbrechen
              </button>
              <button 
                onClick={updateUser}
                disabled={
                  userLoading || 
                  validateUsername(editUsername).length > 0 ||
                  validatePassword(editPassword).length > 0 ||
                  (editUsername === selectedUser.username && 
                   !editPassword && 
                   JSON.stringify(editGroups.sort()) === JSON.stringify((selectedUser.groups?.map(g => g._id) || []).sort()) &&
                   editIsAdmin === (selectedUser.isAdmin || false))
                }
                className="btn btn-primary flex items-center space-x-2"
              >
                {userLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    <span>Speichert...</span>
                  </>
                ) : (
                  <>
                    <FaCog />
                    <span>Änderungen speichern</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
